import { inject, Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CaptureData } from '../api-client/model/captureData';
import { HeaderCaptureData } from '../api-client/model/headerCaptureData';
import { LineItemCaptureData } from '../api-client/model/lineItemCaptureData';

type HeaderCaptureDataForm = Record<keyof HeaderCaptureData, FormGroup>;
type LineItemCaptureDataForm = Record<keyof LineItemCaptureData, FormGroup>;
type CaptureDataProps = keyof HeaderCaptureData | keyof LineItemCaptureData;

const CONFIDENCE_THRESHOLD = 0.6;

export const notNumberValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const controlValue = control.value;
  if (controlValue === null || controlValue === '') {
    return null; // Let required validator handle empty value
  }

  if (isNaN(Number(controlValue))) {
    return { notNumber: true };
  }

  return null;
};

@Injectable({
  providedIn: 'root',
})
export class CaptureDataFormService {
  private readonly formBuilder = inject(FormBuilder);

  buildEmptyFormGroup(): FormGroup {
    return this.formBuilder.group({});
  }

  buildCaptureDataForm(captureData: CaptureData): FormGroup {
    const headerControls = Object.keys(captureData.headers).reduce((acc, key) => {
      const label = key as keyof HeaderCaptureData;
      acc[label] = this.formBuilder.group({
        value: [captureData.headers[label].value ?? '', this.buildValidationsByProp(label)],
        confidence: [captureData.headers[label].confidence ?? 1],
      });
      return acc;
    }, {} as Partial<HeaderCaptureDataForm>);

    const lineItemControls = (captureData.line_items ?? []).map(lineItem =>
      this.formBuilder.group(
        Object.keys(lineItem).reduce((acc, key) => {
          const label = key as keyof LineItemCaptureData;
          acc[label] = this.formBuilder.group({
            value: [lineItem[label].value ?? '', this.buildValidationsByProp(label)],
            confidence: [lineItem[label].confidence ?? 1],
          });
          return acc;
        }, {} as Partial<LineItemCaptureDataForm>),
      ),
    );

    return this.formBuilder.group({
      headers: this.formBuilder.group(headerControls),
      line_items: this.formBuilder.array(lineItemControls),
    });
  }

  buildValidationsByProp(prop: string): ValidatorFn[] {
    const onlyNumberProps: CaptureDataProps[] = [
      'total_vat_included_amount',
      'total_vat_excluded_amount',
      'total_vat_rate_percent',
      'total_vat_amount',
      'delivery_cost',
      'line_number',
      'line_quantity',
      'line_unit_price',
      'line_vat_amount',
      'line_vat_rate_percent',
      'line_total_vat_excluded_amount',
    ];

    const validations = [Validators.required];
    if (onlyNumberProps.includes(prop as CaptureDataProps)) {
      validations.push(notNumberValidator);
    }

    return validations;
  }

  getCaptureFieldValueByProp(form: FormGroup | null, prop: string): string | number | null {
    if (!form) {
      return null;
    }

    const control = form.get(prop);
    if (!control) {
      return null;
    }

    return control.get('value')?.value ?? null;
  }

  getCaptureFieldConfidenceByProp(
    form: FormGroup | null,
    prop: string,
    byDefault = 1,
  ): number | null {
    if (!form) {
      return null;
    }

    const control = form.get(prop);
    if (!control) {
      return null;
    }

    return control.get('confidence')?.value ?? byDefault;
  }

  getCaptureFormGroupByProp(form: FormGroup | null, prop: string): FormGroup {
    if (!form) {
      return this.formBuilder.group({});
    }

    const control = form.get(prop);
    if (!control) {
      return this.formBuilder.group({});
    }
    return control as FormGroup;
  }

  isCaptureDataFieldLowConfident(form: FormGroup, prop: string): boolean {
    const confidence = this.getCaptureFieldConfidenceByProp(form, prop) ?? 0;
    return confidence <= CONFIDENCE_THRESHOLD;
  }

  addLineItemForm(form: FormArray, lineItemProps: string[]) {
    const emptyGroup = this.formBuilder.group(
      lineItemProps.reduce((acc, key) => {
        const label = key as keyof LineItemCaptureData;
        acc[label] = this.formBuilder.group({
          value: ['', this.buildValidationsByProp(label)],
          confidence: [1],
        });
        return acc;
      }, {} as Partial<LineItemCaptureDataForm>),
    );

    form.push(emptyGroup);
  }

  removeLineItemFormByIndex(form: FormArray, index: number) {
    form.removeAt(index);
  }
}
